import React from 'react';
import { Sparkles, Zap, ArrowRight, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import DashboardModal from '@/components/dashboard-modal';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';

interface CreditWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  warningType?: 'low' | 'out';
}

const CreditWarningModal: React.FC<CreditWarningModalProps> = ({
  isOpen,
  onClose,
  warningType = 'low'
}) => {
  const navigate = useNavigate();
  const usageData = useAIUsageData();
  
  const isOutOfCredits = warningType === 'out';

  const handleGoToBilling = () => {
    onClose(); // Close modal first
    navigate('/billing'); // Smooth React Router navigation
  };

  // Use real data from the hook, but override to 0 if explicitly out of credits
  const currentCredits = isOutOfCredits ? 0 : (usageData.remainingBalance || 0);
  const usagePercentage = isOutOfCredits ? 100 : (usageData.usagePercentage || 0);

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title={isOutOfCredits ? "Power Up Your AI Tools" : "Keep Your AI Momentum Going"}
      description={isOutOfCredits ? "Continue using powerful AI features that save you time" : "Stay productive with unlimited AI assistance"}
      size="md"
      accentColor="green"
      showCloseButton={true}
    >
      <div className="space-y-6">
        {/* Success Icon and Status */}
        <div className="flex items-center justify-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-green-100 to-emerald-200 dark:from-green-900/60 dark:to-emerald-800/60 shadow-lg shadow-green-500/20">
            {isOutOfCredits ? (
              <TrendingUp 
                size={44} 
                className="text-green-600 dark:text-green-400" 
              />
            ) : (
              <Sparkles 
                size={40} 
                className="text-green-500" 
              />
            )}
          </div>
        </div>

        {/* Credit Status */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Zap size={20} className="text-green-500" />
            <span className="text-2xl font-bold text-green-600 dark:text-green-400">
              {usageData.isLoading ? 'Loading...' : 
                isOutOfCredits ? 'Ready to Unlock AI Power' : `${currentCredits.toLocaleString()} Credits Available`
              }
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
            {isOutOfCredits 
              ? 'Upgrade your plan to continue using powerful AI features that help you build faster and smarter' 
              : `You have plenty of AI credits! Each action uses credits intelligently - complex generations may use more than simple ones.`
            }
          </p>
        </div>

        {/* AI Services Preview */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-xl p-6 border border-green-200/50 dark:border-green-800/50">
          <div className="space-y-4">
            <div className="text-center">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                Unlock Powerful AI Features
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300 opacity-90">
                Continue building with intelligent assistance
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Smart Research
                </div>
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Instant Images
                </div>
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Content Creation
                </div>
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  AI Assistants
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Inline Footer Button */}
        <div className="flex justify-center pt-4">
          <Button
            onClick={handleGoToBilling}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/25 text-white min-w-[140px] flex items-center gap-2 font-semibold"
          >
            {isOutOfCredits ? 'Upgrade Plan' : 'Add More Credits'}
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </DashboardModal>
  );
};

export default CreditWarningModal; 